/**
 * Typed form for editing a billing plan.
 * Shows diff preview + reason field before confirming.
 */
import { useState } from 'react';
import DiffPreview from './DiffPreview';
import ReasonField from './ReasonField';
import { api } from '../../api';

export default function PlanEditor({ orgId, plan, onSaved }) {
  const [draft, setDraft] = useState({
    plan_type: plan?.plan_type ?? 'passthrough',
    flat_fee_usd: plan?.flat_fee_usd ?? '',
    included_usage_usd: plan?.included_usage_usd ?? '',
    overflow_multiplier: plan?.overflow_multiplier ?? 1.0,
    margin_multiplier: plan?.margin_multiplier ?? 1.0,
    cap_mode: plan?.cap_mode ?? 'none',
    cycle: plan?.cycle ?? 'monthly',
    cycle_anchor_day: plan?.cycle_anchor_day ?? 1,
    auto_index_on_upload: plan?.auto_index_on_upload ?? true,
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  // Coerce form-state values so the diff preview compares apples-to-apples
  // with what we'll actually send on save (empty string → null, numeric
  // strings → numbers). Without this, a null DB value rendered as '' in
  // the input would show up as a phantom change.
  const coerced = {
    plan_type: draft.plan_type,
    flat_fee_usd: draft.flat_fee_usd === '' || draft.flat_fee_usd == null
      ? null
      : Number(draft.flat_fee_usd),
    included_usage_usd: draft.included_usage_usd === '' || draft.included_usage_usd == null
      ? null
      : Number(draft.included_usage_usd),
    overflow_multiplier: Number(draft.overflow_multiplier ?? 1.0),
    margin_multiplier: Number(draft.margin_multiplier ?? 1.0),
    cap_mode: draft.cap_mode,
    cycle: draft.cycle,
    cycle_anchor_day: Number(draft.cycle_anchor_day ?? 1),
    auto_index_on_upload: !!draft.auto_index_on_upload,
  };

  const planBefore = plan ? {
    plan_type: plan.plan_type,
    flat_fee_usd: plan.flat_fee_usd ?? null,
    included_usage_usd: plan.included_usage_usd ?? null,
    overflow_multiplier: Number(plan.overflow_multiplier ?? 1.0),
    margin_multiplier: Number(plan.margin_multiplier ?? 1.0),
    cap_mode: plan.cap_mode,
    cycle: plan.cycle,
    cycle_anchor_day: Number(plan.cycle_anchor_day ?? 1),
    auto_index_on_upload: plan.auto_index_on_upload ?? true,
  } : null;

  async function handleSave() {
    if (!reason.trim()) { setError('Reason is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const result = await api.put(`/ops_admin/orgs/${orgId}/plan`, { ...coerced, reason });
      setEditing(false);
      setReason('');
      onSaved?.(result.plan);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function field(key, label, type = 'text', options = null) {
    if (options) {
      return (
        <div className="form-group" key={key}>
          <label className="form-label">{label}</label>
          <select
            className="form-input"
            value={draft[key]}
            disabled={!editing}
            onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
          >
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      );
    }
    return (
      <div className="form-group" key={key}>
        <label className="form-label">{label}</label>
        <input
          type={type}
          className="form-input"
          value={draft[key] ?? ''}
          disabled={!editing}
          onChange={e => setDraft(d => ({ ...d, [key]: type === 'number' ? parseFloat(e.target.value) : e.target.value }))}
        />
      </div>
    );
  }

  return (
    <div className="plan-editor">
      <div className="plan-editor-header">
        <h3>Billing Plan</h3>
        {!editing && (
          <button className="btn btn-sm btn-secondary" onClick={() => setEditing(true)}>Edit</button>
        )}
      </div>

      <div className="plan-form">
        {field('plan_type', 'Plan Type', 'text', [
          { value: 'passthrough', label: 'Pass-through (per-use)' },
          { value: 'allotment', label: 'Allotment (flat fee + allowance)' },
        ])}
        {field('flat_fee_usd', 'Flat Fee (USD)', 'number')}
        {field('included_usage_usd', 'Included Usage (USD) — the limit a hard cap blocks at', 'number')}
        {field('overflow_multiplier', 'Overflow Multiplier', 'number')}
        {field('margin_multiplier', 'Margin Multiplier', 'number')}
        {field('cap_mode', 'Cap Mode', 'text', [
          { value: 'none', label: 'No cap' },
          { value: 'soft', label: 'Soft cap (warn)' },
          { value: 'hard', label: 'Hard cap (block — only ops can lift)' },
          { value: 'overflow', label: 'Overflow billing' },
        ])}
        {field('cycle', 'Cycle', 'text', [
          { value: 'monthly', label: 'Monthly' },
          { value: 'per_case', label: 'Per case' },
        ])}

        {/* Document Readiness — auto-index lever (cost-control, not privacy) */}
        <div className="form-group" key="auto_index_on_upload">
          <label className="form-label">Auto-index on upload</label>
          <label
            className="form-checkbox"
            title="When on, every uploaded doc lands at Surface search tier automatically. When off, docs stay at Skim until users explicitly opt in."
          >
            <input
              type="checkbox"
              checked={!!draft.auto_index_on_upload}
              disabled={!editing}
              onChange={e =>
                setDraft(d => ({ ...d, auto_index_on_upload: e.target.checked }))
              }
            />
            <span>{draft.auto_index_on_upload ? 'On — surface tier by default' : 'Off — skim tier by default'}</span>
          </label>
        </div>
      </div>

      {editing && (
        <div className="plan-editor-confirm">
          <DiffPreview before={planBefore} after={coerced} label="You are about to change:" />
          <ReasonField value={reason} onChange={setReason} />
          {error && <p className="error-text">{error}</p>}
          <div className="button-row">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Plan'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setEditing(false); setReason(''); setError(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
